import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion } from 'framer-motion';

const MobileNavLink = ({ to, children, onClick }: { to: string; children: React.ReactNode; onClick?: () => void }) => (
  <Link 
    to={to}
    onClick={onClick}
    className="block px-4 py-4 font-medium text-gray-300 hover:text-white hover:bg-gradient-to-r hover:from-blue-600/20 hover:to-cyan-600/20 rounded-lg transition-colors active:bg-white/20 touch-manipulation"
  >
    {children}
  </Link>
);

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  return (
    <nav className={`fixed w-full z-[100] transition-all duration-300 ${
      isScrolled || isMenuOpen ? 'bg-black shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 sm:h-20">
          <button 
            className="text-white p-3 -ml-3 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors touch-manipulation"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <Link to="/" className="flex items-center space-x-2 mx-auto" onClick={() => setIsMenuOpen(false)}>
            <span className="text-base sm:text-2xl font-bold text-white">FITNESS PORTAL</span>
          </Link>

          {/* Empty div to balance the layout */}
          <div className="w-10 h-10" />
        </div>

        {/* CTA Button */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden lg:block">
          <a
            href="/memberships"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-2 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105"
          >
            <span>JOIN TODAY</span>
          </a>
        </div>
      </div>

      {/* Mobile menu */}
      <motion.div 
        initial={false}
        animate={{ height: isMenuOpen ? 'auto' : 0, opacity: isMenuOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className={`fixed inset-x-0 top-16 sm:top-20 bg-black border-t border-white/10 overflow-hidden ${isMenuOpen ? 'bottom-0' : ''}`}
        style={{ display: isMenuOpen ? 'block' : 'none' }}
      >
        <div className="px-4 py-2 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <MobileNavLink to="/memberships" onClick={() => setIsMenuOpen(false)}>
            <span className="text-lg">Memberships</span>
          </MobileNavLink>
          <MobileNavLink to="/trainers" onClick={() => setIsMenuOpen(false)}>
            <span className="text-lg">Trainers</span>
          </MobileNavLink>
          <MobileNavLink to="/classes" onClick={() => setIsMenuOpen(false)}>
            <span className="text-lg">Classes</span>
          </MobileNavLink>
          <MobileNavLink to="/recovery" onClick={() => setIsMenuOpen(false)}>
            <span className="text-lg">Recovery Zone</span>
          </MobileNavLink>
          <MobileNavLink to="/pt-course" onClick={() => setIsMenuOpen(false)}>
            <span className="text-lg">PT Course</span>
          </MobileNavLink>
          <MobileNavLink to="/fitness-passport" onClick={() => setIsMenuOpen(false)}>
            <span className="text-lg">Fitness Passport</span>
          </MobileNavLink>
          <MobileNavLink to="/corporate" onClick={() => setIsMenuOpen(false)}>
            <span className="text-lg pl-4 text-gray-400 hover:text-white">Corporate</span>
          </MobileNavLink>
          <MobileNavLink to="/faq" onClick={() => setIsMenuOpen(false)}>
            <span className="text-lg">FAQ</span>
          </MobileNavLink>
          <MobileNavLink to="/contact" onClick={() => setIsMenuOpen(false)}>
            <span className="text-lg">Contact</span>
          </MobileNavLink>
          <div className="pt-4 pb-safe">
            <a 
              href="/memberships"
              onClick={() => setIsMenuOpen(false)}
              className="block w-full bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-4 text-white font-bold rounded-lg text-center hover:from-blue-700 hover:to-cyan-700 active:from-blue-800 active:to-cyan-800 transition-all duration-300 touch-manipulation"
            >
              <span className="text-lg">JOIN TODAY</span>
            </a>
          </div>
        </div>
      </motion.div>
    </nav>
  );
}